import { DOCUMENT } from '@angular/common';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import type { ApplicationConfig } from '@angular/core';
import { GoogleTagManagerConfigService } from 'angular-google-tag-manager';
import { environment } from '../environments/environment';
import { inject, isDevMode, provideAppInitializer, provideBrowserGlobalErrorListeners, provideEnvironmentInitializer } from '@angular/core';
import { provideNativeDateAdapter } from '@angular/material/core';
import { provideClientHydration, withEventReplay, withNoIncrementalHydration } from '@angular/platform-browser';
import { provideRouter, TitleStrategy, withNavigationErrorHandler } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { provideEffects } from '@ngrx/effects';
import { provideRouterStore, routerReducer } from '@ngrx/router-store';
import { provideState, provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { routes } from './app.routes';
import { appSettingsInitializer } from '@app/domain/initializers/app-settings.initializer';
import { homeScreenInstallInitializer } from '@app/domain/initializers/home-screen-install.initializer';
import { pusherInitializer } from '@app/domain/initializers/pusher.initializer';
import { setUserTokenInitializer } from '@app/domain/initializers/set-user-token.initializer';
import { userDetailsInitializer } from '@app/domain/initializers/user-details.initializer';
import { GTM_COOKIES_WIN } from '@app/domain/properties/gtm.props';
import { authInterceptor } from '@app/routing/interceptors/auth.interceptor';
import { platformInterceptor } from '@app/routing/interceptors/platform.interceptor';
import { unauthorizedInterceptor } from '@app/routing/interceptors/unauthorized.interceptor';
import { Icon } from '@app/services/icon/icon';
import { TitleStrategyService } from '@app/services/title-strategy/title-strategy';
import { howToPlayEffects } from '@app/store/tutorial/howToPlay.effects';
import { tutorialEffects } from '@app/store/tutorial/tutorial.effects';
import { userEffects } from '@app/store/user/user.effects';
import { userFeature } from '@app/store/user/user.feature';

function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;

  return (
    error.name === 'ChunkLoadError'
    || /Failed to fetch dynamically imported module/i.test(error.message)
    || /Importing a module script failed/i.test(error.message)
    || /Loading chunk .* failed/i.test(error.message)
  );
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideRouter(routes, withNavigationErrorHandler(e => {
      if (isChunkLoadError(e.error)) {
        inject(DOCUMENT).location.reload();
      }
    })),
    provideHttpClient(
      withFetch(),
      withInterceptors([authInterceptor, platformInterceptor, unauthorizedInterceptor]),
    ),
    provideClientHydration(withEventReplay(), withNoIncrementalHydration()),
    provideStore(
      {
        router: routerReducer,
      },
    ),
    provideState(userFeature),
    provideEffects([tutorialEffects, howToPlayEffects, userEffects]),
    provideRouterStore(),
    provideStoreDevtools({
      maxAge: 25,
      logOnly: !isDevMode(),
      autoPause: true,
      trace: false,
      traceLimit: 75,
    }),
    {
      provide: TitleStrategy,
      useClass: TitleStrategyService,
    },
    provideAppInitializer(homeScreenInstallInitializer),
    provideAppInitializer(pusherInitializer),
    provideAppInitializer(setUserTokenInitializer),
    provideAppInitializer(userDetailsInitializer),
    provideAppInitializer(appSettingsInitializer),
    provideEnvironmentInitializer(() => {
      inject(Icon); // todo: ask
    }),
    provideNativeDateAdapter(),

    /* Config token rather than `provideGoogleTagManager()`: that helper forwards only id/auth/preview,
       while every key on this object becomes a query param of gtm.js — needed for `gtm_cookies_win`. */
    {
      provide: GoogleTagManagerConfigService,
      useValue: {
        id: environment.gtmId,
        gtm_auth: environment.gtmAuth ?? undefined,
        gtm_preview: environment.gtmPreview ?? undefined,
        gtm_cookies_win: environment.gtmPreview ? GTM_COOKIES_WIN : undefined,
      },
    },
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000',
    }),
  ],
};
